import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Forward,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  Paperclip,
  PenSquare,
  Plus,
  RefreshCw,
  Reply,
  ReplyAll,
  Search,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { getSafeBackUrl } from "@/utils";

// Back to the firm's own portal.
//
// Both back links pointed at createPageUrl("Home"), which is /Home - and vercel.json
// redirects /Home to the client-login page on the public site. Pressing Back threw the
// lawyer out of the portal and asked them to sign in again. getSafeBackUrl already works
// out the right portal for the brand in the address; the fallback covers local work, where
// there is no completelawsupport origin to read.
const backToPortal = () => getSafeBackUrl(null) || "/lacw-portal";
import {
  deleteMicrosoftMessage,
  downloadMicrosoftAttachment,
  forwardMicrosoftMessage,
  getMicrosoftMailboxProfile,
  getMicrosoftMessage,
  listMicrosoftMailFolders,
  listMicrosoftMessageAttachments,
  listMicrosoftMessages,
  MAILBOX_FOLDERS,
  moveMicrosoftMessage,
  replyToMicrosoftMessage,
  requestMicrosoftMailAccessToken,
  sendMicrosoftMessage,
  setMicrosoftMessageRead,
} from "@/lib/microsoftMailClient";
import {
  createOutlookCalendarEvent,
  deleteOutlookCalendarEvent,
  listOutlookCalendarEvents,
  listOutlookCalendars,
  updateOutlookCalendarEvent,
} from "@/lib/microsoftCalendarClient";
import OutlookAppearancePane from "@/pages/OutlookAppearancePane";
import {
  appointmentLocationFromListing,
  appointmentTimesFromListing,
  appointmentTitleFromListing,
} from "@/lib/appointmentFromListing";

const TOKEN_KEY = "clsConnectMicrosoftMailToken";
const splitAddresses = (value) =>
  value
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
const address = (person) => person?.emailAddress?.address || "";
const personLabel = (person) =>
  person?.emailAddress?.name || address(person) || "Unknown sender";

function formatMessageDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function saveAttachment(attachment) {
  const binary = atob(attachment.contentBytes);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(
    new Blob([bytes], {
      type: attachment.contentType || "application/octet-stream",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = attachment.name || "attachment";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function ComposePanel({
  mode,
  message,
  initialDraft,
  onClose,
  onSent,
  accessToken,
}) {
  const isForward = mode === "forward";
  const isReply = mode === "reply" || mode === "replyAll";
  const [to, setTo] = useState(
    isReply
      ? address(message?.from)
      : (initialDraft?.toRecipients || []).join("; "),
  );
  const [cc, setCc] = useState(
    mode === "replyAll"
      ? (message?.toRecipients || []).map(address).filter(Boolean).join("; ")
      : "",
  );
  const [subject, setSubject] = useState(
    initialDraft?.subject ||
      (isReply
        ? `RE: ${message?.subject || ""}`
        : isForward
          ? `FW: ${message?.subject || ""}`
          : ""),
  );
  const [body, setBody] = useState(initialDraft?.body || "");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  const addFiles = async (event) => {
    const files = [...event.target.files];
    const tooLarge = files.find((file) => file.size > 3 * 1024 * 1024);
    if (tooLarge)
      return toast.error(
        `${tooLarge.name} is over the 3 MB direct attachment limit.`,
      );
    const mapped = await Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
              resolve({
                name: file.name,
                contentType: file.type,
                contentBytes: String(reader.result).split(",")[1],
              });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          }),
      ),
    );
    setAttachments((current) => [...current, ...mapped]);
    event.target.value = "";
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!body.trim() && !attachments.length)
      return toast.error("Add a message before sending.");
    if ((!isReply || isForward) && !splitAddresses(to).length)
      return toast.error("Enter at least one recipient.");
    setSending(true);
    try {
      if (mode === "reply" || mode === "replyAll") {
        await replyToMicrosoftMessage(
          accessToken,
          message.id,
          body,
          mode === "replyAll",
        );
      } else if (isForward) {
        await forwardMicrosoftMessage(
          accessToken,
          message.id,
          splitAddresses(to),
          body,
        );
      } else {
        await sendMicrosoftMessage(accessToken, {
          to: splitAddresses(to),
          cc: splitAddresses(cc),
          subject,
          body,
          attachments,
        });
      }
      toast.success("Message sent through Outlook.");
      onSent();
    } catch (error) {
      toast.error(error.message || "Outlook could not send the message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Compose Outlook message"
    >
      <form
        onSubmit={submit}
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
      >
        <header className="flex items-center justify-between bg-blue-700 px-5 py-4 text-white">
          <h2 className="font-bold">
            {isReply
              ? mode === "replyAll"
                ? "Reply all"
                : "Reply"
              : isForward
                ? "Forward"
                : "New message"}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close compose">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="space-y-3 overflow-y-auto p-5">
          {!isReply && (
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="To (separate addresses with ; )"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          )}
          {!isReply && !isForward && (
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="Cc"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          )}
          {!isReply && !isForward && (
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            autoFocus
            placeholder="Write your message…"
            className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm leading-6"
          />
          {!isReply && !isForward && (
            <div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Paperclip className="h-4 w-4" /> Attach files{" "}
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={addFiles}
                />
              </label>
              {!!attachments.length && (
                <p className="mt-2 text-xs text-slate-500">
                  {attachments.map((item) => item.name).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>
        <footer className="flex justify-end gap-3 border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600"
          >
            Cancel
          </button>
          <button
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}{" "}
            Send
          </button>
        </footer>
      </form>
    </div>
  );
}

const isoDate = (date) => date.toISOString().slice(0, 10);
const localInput = (value) => (value ? value.slice(0, 16) : "");

function CalendarEditor({
  event,
  calendarId,
  calendars,
  accessToken,
  onClose,
  onSaved,
}) {
  const [subject, setSubject] = useState(event?.subject || "");
  const [start, setStart] = useState(
    localInput(event?.start?.dateTime) || `${isoDate(new Date())}T09:00`,
  );
  const [end, setEnd] = useState(
    localInput(event?.end?.dateTime) || `${isoDate(new Date())}T10:00`,
  );
  const [location, setLocation] = useState(event?.location?.displayName || "");

  // Which fields the person has taken charge of. Anything they have typed, or anything an
  // existing appointment already carried, is theirs: the listing fills in blanks, it does
  // not correct people. Overwriting a typed title while someone is still working would be
  // worse than not helping at all, because they would have to notice it happened.
  const [titleTouched, setTitleTouched] = useState(Boolean(event?.subject));
  const [locationTouched, setLocationTouched] = useState(
    Boolean(event?.location?.displayName),
  );
  const [timesTouched, setTimesTouched] = useState(Boolean(event?.start?.dateTime));

  // CLS Connect hands its save up here and this dialog draws the only button.
  //
  // Both used to write their own calendar entry, so an appointment filled in on both halves
  // of the screen arrived in Outlook twice. One button now saves once: through CLS Connect
  // when a matter is linked, so the appearance block and the billing draft still happen, and
  // as a plain appointment when there is none - a meeting with nothing to file against still
  // has to be possible here.
  const paneSave = useRef(null);
  const [hasMatter, setHasMatter] = useState(false);
  const registerPaneSave = useCallback((fn) => {
    paneSave.current = fn;
  }, []);

  // Called by the appearance pane below whenever the listing changes.
  const applyListing = useCallback(
    (listing) => {
      if (!listing) return;

      setHasMatter(Boolean(listing.matterId || listing.clientName));

      if (!titleTouched) {
        const title = appointmentTitleFromListing(listing);
        if (title) setSubject(title);
      }
      if (!locationTouched) {
        const where = appointmentLocationFromListing(listing);
        if (where) setLocation(where);
      }
      if (!timesTouched) {
        const times = appointmentTimesFromListing(listing);
        if (times) {
          setStart(times.start);
          setEnd(times.end);
        }
      }
    },
    [titleTouched, locationTouched, timesTouched],
  );
  const [notes, setNotes] = useState(
    event?.body?.content || event?.bodyPreview || "",
  );
  const [saving, setSaving] = useState(false);
  const [clerkCompose, setClerkCompose] = useState(null);
  const microsoftSession = useMemo(
    () => ({ accessToken, calendars, calendarId }),
    [accessToken, calendars, calendarId],
  );

  const submit = async () => {
    // A matter means CLS Connect owns this appointment: it writes the calendar entry, the
    // appearance block and the billing draft. Saving here too would be the second one.
    if (hasMatter && paneSave.current) {
      setSaving(true);
      try {
        await paneSave.current();
        onSaved?.();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "The appointment could not be saved.",
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    // The appointment details panel is gone: CLS Connect supplies the appointment now, and
    // it needs a matter. Say that, rather than complaining about a title field that is no
    // longer on screen.
    if (!hasMatter) {
      return toast.error("Choose an Actionstep matter below before saving this appointment.");
    }

    if (!subject.trim()) return toast.error("Add an appointment title.");
    if (new Date(end) <= new Date(start))
      return toast.error("The end time must be after the start time.");
    setSaving(true);
    const payload = {
      subject: subject.trim(),
      start: { dateTime: start, timeZone: "Australia/Sydney" },
      end: { dateTime: end, timeZone: "Australia/Sydney" },
      location: { displayName: location.trim() },
      body: { contentType: "Text", content: notes },
    };
    try {
      if (event?.id)
        await updateOutlookCalendarEvent(
          accessToken,
          calendarId,
          event.id,
          payload,
        );
      else await createOutlookCalendarEvent(accessToken, calendarId, payload);
      toast.success(
        event?.id
          ? "Outlook appointment updated."
          : "Outlook appointment created.",
      );
      onSaved();
    } catch (error) {
      toast.error(error.message || "The appointment could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end bg-slate-950/40 sm:place-items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Outlook appointment editor"
    >
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <header className="flex items-center justify-between bg-blue-700 px-5 py-4 text-white">
          <h2 className="font-bold">
            {event?.id ? "Edit appointment" : "Create appointment"}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="grid min-h-0 flex-1 overflow-y-auto">
          <div className="bg-slate-50 p-3 sm:p-5">
            <div className="mb-3 rounded-xl border border-purple-200 bg-purple-50 p-3">
              <p className="text-sm font-bold text-purple-950">CLS Connect</p>
              <p className="mt-1 text-xs text-purple-800">
                Link this event to Actionstep, record the appearance and prepare
                the next steps.
              </p>
            </div>
            <OutlookAppearancePane
              portalMode
              portalMicrosoftSession={microsoftSession}
              portalEvent={event?.id ? event : null}
              portalComposeEmail={setClerkCompose}
              portalOnListingChange={applyListing}
              portalRegisterSave={registerPaneSave}
            />
          </div>
        </div>
        <footer className="flex justify-end gap-3 border-t border-slate-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-5 py-2 text-sm font-bold text-white disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            appointment
          </button>
        </footer>
      </div>
      {clerkCompose && (
        <ComposePanel
          mode="new"
          initialDraft={clerkCompose}
          accessToken={accessToken}
          onClose={() => setClerkCompose(null)}
          onSent={() => setClerkCompose(null)}
        />
      )}
    </div>
  );
}

function CalendarPanel({ accessToken, emailDraft, clearEmailDraft }) {
  const [calendars, setCalendars] = useState([]);
  const [calendarId, setCalendarId] = useState("");
  const [events, setEvents] = useState([]);
  const [anchor, setAnchor] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const rangeStart = useMemo(() => {
    const date = new Date(anchor);
    date.setDate(date.getDate() - 7);
    return isoDate(date);
  }, [anchor]);
  const rangeEnd = useMemo(() => {
    const date = new Date(anchor);
    date.setDate(date.getDate() + 35);
    return isoDate(date);
  }, [anchor]);

  const loadEvents = useCallback(
    async (token, selectedId, from = rangeStart, to = rangeEnd) => {
      if (!selectedId) return;
      setLoading(true);
      try {
        setEvents(await listOutlookCalendarEvents(token, selectedId, from, to));
      } catch (error) {
        toast.error(error.message || "Outlook Calendar could not be loaded.");
      } finally {
        setLoading(false);
      }
    },
    [rangeEnd, rangeStart],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const available = await listOutlookCalendars(accessToken);
        if (!active) return;
        setCalendars(available);
        const selectedId =
          available.find((item) => item.primary)?.id || available[0]?.id || "";
        setCalendarId(selectedId);
        await loadEvents(accessToken, selectedId);
      } catch (error) {
        if (active)
          toast.error(error.message || "Outlook Calendar could not be opened.");
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken]);

  useEffect(() => {
    if (emailDraft) setEditor(emailDraft);
  }, [emailDraft]);
  const closeEditor = () => {
    setEditor(null);
    clearEmailDraft?.();
  };
  const shift = async (days) => {
    const next = new Date(anchor);
    next.setDate(next.getDate() + days);
    setAnchor(next);
    const from = new Date(next);
    from.setDate(from.getDate() - 7);
    const to = new Date(next);
    to.setDate(to.getDate() + 35);
    await loadEvents(accessToken, calendarId, isoDate(from), isoDate(to));
  };
  const remove = async (event) => {
    if (
      !window.confirm(
        `Delete “${event.subject || "this appointment"}” from Outlook?`,
      )
    )
      return;
    try {
      await deleteOutlookCalendarEvent(accessToken, calendarId, event.id);
      await loadEvents(accessToken, calendarId);
      toast.success("Appointment deleted from Outlook.");
    } catch (error) {
      toast.error(error.message || "The appointment could not be deleted.");
    }
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-slate-50">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-5 py-4">
        <select
          value={calendarId}
          onChange={(e) => {
            setCalendarId(e.target.value);
            loadEvents(accessToken, e.target.value);
          }}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          {calendars.map((calendar) => (
            <option key={calendar.id} value={calendar.id}>
              {calendar.summary}
              {calendar.primary ? " (default)" : ""}
            </option>
          ))}
        </select>
        <div className="flex items-center rounded-lg border border-slate-300">
          <button
            onClick={() => shift(-7)}
            className="p-2"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              const now = new Date();
              setAnchor(now);
              loadEvents(
                accessToken,
                calendarId,
                isoDate(now),
                isoDate(new Date(now.getTime() + 35 * 86400000)),
              );
            }}
            className="border-x border-slate-300 px-3 py-2 text-xs font-bold"
          >
            Today
          </button>
          <button
            onClick={() => shift(7)}
            className="p-2"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Agenda: {rangeStart} to {rangeEnd}
        </p>
        <button
          onClick={() => setEditor({})}
          className="ml-auto inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white"
        >
          <Plus className="h-4 w-4" /> Create appointment
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <div className="grid h-40 place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
          </div>
        ) : events.length ? (
          <div className="mx-auto max-w-4xl space-y-3">
            {events.map((event) => (
              <article
                key={event.id}
                className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center"
              >
                <div className="w-24 flex-none">
                  <p className="text-xs font-bold text-blue-700">
                    {new Date(event.start?.dateTime).toLocaleDateString(
                      "en-AU",
                      { weekday: "short", day: "numeric", month: "short" },
                    )}
                  </p>
                  <p className="text-sm font-semibold">
                    {event.start?.dateTime?.slice(11, 16)}–
                    {event.end?.dateTime?.slice(11, 16)}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-bold">
                    {event.subject || "(No title)"}
                  </h3>
                  <p className="truncate text-xs text-slate-500">
                    {event.location?.displayName ||
                      event.bodyPreview ||
                      "Outlook appointment"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditor(event)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(event)}
                    className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                    aria-label="Delete appointment"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <CalendarDays className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-600">
              No appointments in this period
            </p>
          </div>
        )}
      </div>
      {editor && (
        <CalendarEditor
          event={editor}
          calendarId={calendarId}
          calendars={calendars}
          accessToken={accessToken}
          onClose={closeEditor}
          onSaved={async () => {
            closeEditor();
            await loadEvents(accessToken, calendarId);
          }}
        />
      )}
    </section>
  );
}

export default function OutlookMail() {
  const [accessToken, setAccessToken] = useState(
    () => window.sessionStorage.getItem(TOKEN_KEY) || "",
  );
  const [profile, setProfile] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [compose, setCompose] = useState(null);
  const [surface, setSurface] = useState("mail");
  const [emailCalendarDraft, setEmailCalendarDraft] = useState(null);
  const visibleFolders = useMemo(() => {
    const folderByName = new Map(
      folders.map((folder) => [folder.displayName?.toLowerCase(), folder]),
    );
    return MAILBOX_FOLDERS.map((known) => {
      const graphFolder = folderByName.get(known.label.toLowerCase());
      return { ...known, unread: graphFolder?.unreadItemCount || 0 };
    });
  }, [folders]);

  const loadMailbox = useCallback(
    async (token, targetFolder = folderId, search = activeQuery) => {
      setLoading(true);
      try {
        const [mailProfile, folderPayload, result] = await Promise.all([
          profile
            ? Promise.resolve(profile)
            : getMicrosoftMailboxProfile(token),
          folders.length
            ? Promise.resolve(folders)
            : listMicrosoftMailFolders(token),
          listMicrosoftMessages(token, { folderId: targetFolder, search }),
        ]);
        setProfile(mailProfile);
        setFolders(folderPayload);
        setMessages(result.messages);
      } catch (error) {
        if (/401|InvalidAuthenticationToken/i.test(error.message)) {
          window.sessionStorage.removeItem(TOKEN_KEY);
          setAccessToken("");
        }
        toast.error(error.message || "Outlook Mail could not be loaded.");
      } finally {
        setLoading(false);
      }
    },
    [activeQuery, folderId, folders, profile],
  );

  useEffect(() => {
    if (accessToken) loadMailbox(accessToken);
  }, [accessToken]); // connection bootstrap only

  const connect = async () => {
    setConnecting(true);
    try {
      const token = await requestMicrosoftMailAccessToken();
      window.sessionStorage.setItem(TOKEN_KEY, token);
      setAccessToken(token);
      toast.success("Outlook Mail connected for this session.");
    } catch (error) {
      toast.error(error.message || "Microsoft sign-in could not be completed.");
    } finally {
      setConnecting(false);
    }
  };

  const chooseFolder = async (id) => {
    setFolderId(id);
    setSelected(null);
    setAttachments([]);
    setActiveQuery("");
    setQuery("");
    await loadMailbox(accessToken, id, "");
  };

  const chooseMessage = async (message) => {
    try {
      const detail = await getMicrosoftMessage(accessToken, message.id);
      setSelected(detail);
      setAttachments(
        detail.hasAttachments
          ? await listMicrosoftMessageAttachments(accessToken, detail.id)
          : [],
      );
      if (!detail.isRead) {
        await setMicrosoftMessageRead(accessToken, detail.id, true);
        setMessages((current) =>
          current.map((item) =>
            item.id === detail.id ? { ...item, isRead: true } : item,
          ),
        );
        setSelected((current) => ({ ...current, isRead: true }));
      }
    } catch (error) {
      toast.error(error.message || "That message could not be opened.");
    }
  };

  const applySearch = async (event) => {
    event.preventDefault();
    setActiveQuery(query.trim());
    setSelected(null);
    await loadMailbox(accessToken, folderId, query.trim());
  };

  const performMessageAction = async (action, success) => {
    if (!selected) return;
    try {
      await action();
      setSelected(null);
      setAttachments([]);
      await loadMailbox(accessToken, folderId, activeQuery);
      toast.success(success);
    } catch (error) {
      toast.error(error.message || "Outlook could not update that message.");
    }
  };

  const download = async (item) => {
    try {
      saveAttachment(
        await downloadMicrosoftAttachment(accessToken, selected.id, item.id),
      );
    } catch (error) {
      toast.error(error.message || "Attachment download failed.");
    }
  };

  if (!accessToken)
    return (
      <main className="grid min-h-[calc(100vh-64px)] place-items-center bg-slate-50 p-6">
        <section className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-blue-700 text-white">
            <Mail className="h-7 w-7" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-slate-900">
            Outlook Mail &amp; Calendar
          </h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Read email and manage appointments through your Microsoft account.
            CLS Connect never receives your password, and this connection lasts
            only for this browser session.
          </p>
          <button
            onClick={connect}
            disabled={connecting}
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-3 text-sm font-bold text-white disabled:opacity-60"
          >
            {connecting && <Loader2 className="h-4 w-4 animate-spin" />} Connect
            Microsoft Outlook
          </button>
          <a
            href={backToPortal()}
            className="mt-5 block text-sm font-semibold text-slate-500 hover:text-blue-700"
          >
            Return to portal
          </a>
        </section>
      </main>
    );

  return (
    <main className="flex min-h-[calc(100vh-64px)] flex-col bg-slate-100 text-slate-900">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <a
          href={backToPortal()}
          aria-label="Back to portal"
          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </a>
        <div>
          <h1 className="font-bold">Outlook Mail &amp; Calendar</h1>
          <p className="text-xs text-slate-500">
            {profile?.displayName || profile?.mail || "Microsoft 365"}
          </p>
        </div>
        <div className="flex rounded-lg bg-slate-100 p-1">
          <button
            onClick={() => setSurface("mail")}
            className={`rounded-md px-3 py-1.5 text-xs font-bold ${surface === "mail" ? "bg-white text-blue-800 shadow-sm" : "text-slate-500"}`}
          >
            <Mail className="mr-1 inline h-3.5 w-3.5" />
            Mail
          </button>
          <button
            onClick={() => setSurface("calendar")}
            className={`rounded-md px-3 py-1.5 text-xs font-bold ${surface === "calendar" ? "bg-white text-blue-800 shadow-sm" : "text-slate-500"}`}
          >
            <CalendarDays className="mr-1 inline h-3.5 w-3.5" />
            Calendar
          </button>
        </div>
        {surface === "mail" && (
          <form
            onSubmit={applySearch}
            className="relative ml-auto w-full sm:w-80"
          >
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this folder"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm"
            />
          </form>
        )}
        {surface === "mail" && (
          <button
            onClick={() => loadMailbox(accessToken)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
        {surface === "mail" && (
          <button
            onClick={() => setCompose({ mode: "new" })}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-sm font-bold text-white"
          >
            <PenSquare className="h-4 w-4" /> New message
          </button>
        )}
      </header>
      {surface === "calendar" ? (
        <CalendarPanel
          accessToken={accessToken}
          emailDraft={emailCalendarDraft}
          clearEmailDraft={() => setEmailCalendarDraft(null)}
        />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[190px_330px_minmax(0,1fr)]">
          <nav className="flex gap-2 overflow-x-auto border-b border-slate-200 bg-white p-3 md:flex-col md:border-b-0 md:border-r">
            {visibleFolders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => chooseFolder(folder.id)}
                className={`flex min-w-fit items-center justify-between gap-4 rounded-lg px-3 py-2 text-left text-sm font-semibold ${folderId === folder.id ? "bg-blue-50 text-blue-800" : "text-slate-600 hover:bg-slate-50"}`}
              >
                <span className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" />
                  {folder.label}
                </span>
                {folder.unread > 0 && (
                  <span className="rounded-full bg-blue-100 px-2 text-xs">
                    {folder.unread}
                  </span>
                )}
              </button>
            ))}
            <button
              onClick={() => {
                window.sessionStorage.removeItem(TOKEN_KEY);
                setAccessToken("");
              }}
              className="mt-auto rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-500 hover:bg-slate-50"
            >
              Disconnect Outlook
            </button>
          </nav>
          <section
            className="max-h-[42vh] overflow-y-auto border-b border-slate-200 bg-white md:max-h-none md:border-b-0 md:border-r"
            aria-label="Messages"
          >
            {loading && !messages.length ? (
              <div className="grid h-40 place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-700" />
              </div>
            ) : messages.length ? (
              messages.map((message) => (
                <button
                  key={message.id}
                  onClick={() => chooseMessage(message)}
                  className={`block w-full border-b border-slate-100 p-4 text-left hover:bg-slate-50 ${selected?.id === message.id ? "bg-blue-50" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-2 h-2 w-2 flex-none rounded-full ${message.isRead ? "bg-transparent" : "bg-blue-600"}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex justify-between gap-2">
                        <p
                          className={`truncate text-sm ${message.isRead ? "font-medium" : "font-bold"}`}
                        >
                          {personLabel(message.from)}
                        </p>
                        <time className="flex-none text-[11px] text-slate-400">
                          {formatMessageDate(
                            message.receivedDateTime || message.sentDateTime,
                          )}
                        </time>
                      </div>
                      <p
                        className={`mt-1 truncate text-sm ${message.isRead ? "text-slate-600" : "font-semibold"}`}
                      >
                        {message.subject || "(No subject)"}
                      </p>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-500">
                        {message.bodyPreview}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-8 text-center text-sm text-slate-500">
                No messages found.
              </div>
            )}
          </section>
          <article className="min-w-0 overflow-y-auto bg-white p-5 md:p-7">
            {selected ? (
              <>
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-5">
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold">
                      {selected.subject || "(No subject)"}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                      From: {personLabel(selected.from)} &lt;
                      {address(selected.from)}&gt;
                    </p>
                    <p className="text-xs text-slate-400">
                      {formatMessageDate(
                        selected.receivedDateTime || selected.sentDateTime,
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => {
                        const start = new Date();
                        start.setMinutes(
                          Math.ceil(start.getMinutes() / 30) * 30,
                          0,
                          0,
                        );
                        const end = new Date(start.getTime() + 60 * 60 * 1000);
                        setEmailCalendarDraft({
                          subject: selected.subject || "Follow-up",
                          start: { dateTime: start.toISOString().slice(0, 16) },
                          end: { dateTime: end.toISOString().slice(0, 16) },
                          body: {
                            content: `Created from email by ${personLabel(selected.from)}\n\n${selected.bodyPreview || ""}`,
                          },
                        });
                        setSurface("calendar");
                      }}
                      title="Create calendar appointment"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <CalendarDays className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCompose({ mode: "reply", message: selected })
                      }
                      title="Reply"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Reply className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCompose({ mode: "replyAll", message: selected })
                      }
                      title="Reply all"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <ReplyAll className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        setCompose({ mode: "forward", message: selected })
                      }
                      title="Forward"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Forward className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        performMessageAction(
                          () =>
                            setMicrosoftMessageRead(
                              accessToken,
                              selected.id,
                              !selected.isRead,
                            ),
                          selected.isRead ? "Marked unread." : "Marked read.",
                        )
                      }
                      title="Toggle read"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      {selected.isRead ? (
                        <Mail className="h-4 w-4" />
                      ) : (
                        <MailOpen className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() =>
                        performMessageAction(
                          () =>
                            moveMicrosoftMessage(
                              accessToken,
                              selected.id,
                              "archive",
                            ),
                          "Message archived.",
                        )
                      }
                      title="Archive"
                      className="rounded-lg p-2 hover:bg-slate-100"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() =>
                        performMessageAction(
                          () =>
                            deleteMicrosoftMessage(accessToken, selected.id),
                          "Message moved to Deleted Items.",
                        )
                      }
                      title="Delete"
                      className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {!!attachments.length && (
                  <div className="flex flex-wrap gap-2 border-b border-slate-200 py-4">
                    {attachments
                      .filter((item) => !item.isInline)
                      .map((item) => (
                        <button
                          key={item.id}
                          onClick={() => download(item)}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold hover:bg-slate-50"
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                          {item.name}
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      ))}
                  </div>
                )}
                <pre className="whitespace-pre-wrap break-words py-6 font-sans text-sm leading-7 text-slate-700">
                  {selected.body?.content || selected.bodyPreview}
                </pre>
              </>
            ) : (
              <div className="grid h-full min-h-64 place-items-center text-center">
                <div>
                  <Mail className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-3 font-semibold text-slate-600">
                    Select a message to read it
                  </p>
                  <p className="mt-1 text-sm text-slate-400">
                    Your Outlook mailbox stays in Microsoft 365.
                  </p>
                </div>
              </div>
            )}
          </article>
        </div>
      )}
      {compose && (
        <ComposePanel
          {...compose}
          accessToken={accessToken}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            loadMailbox(accessToken, folderId, activeQuery);
          }}
        />
      )}
    </main>
  );
}
